import Divider from '@ahamove/web-components/lib/Divider';
import ListItem from '@ahamove/web-components/lib/ListItem';
import ListItemAvatar from '@ahamove/web-components/lib/ListItemAvatar';
import ListItemButton from '@ahamove/web-components/lib/ListItemButton';
import ListItemText from '@ahamove/web-components/lib/ListItemText';
import Radio from '@ahamove/web-components/lib/Radio';
import Stack from '@ahamove/web-components/lib/Stack';
import Typography from '@ahamove/web-components/lib/Typography';
import cn from 'classnames';
import dayjs from 'dayjs';
import Image from 'next/legacy/image';
import { useGetUserProfile } from '@/api/auth/useGetUserProfile';
import { useDateTimeStore } from '@/global-stores/useDateTimeStore';
import { useEVStore } from '@/global-stores/useEVStore';
import { useLocationStore } from '@/global-stores/useLocationStore';
import { detectPlatform } from '@/lib/common';
import { DEFAULT_DATE_TIME_FORMAT, EV_TRACK_KEY } from '@/lib/constants';
import { trackEvent } from '@/lib/eventTracking';
import useIsMobile from '@/shared-hooks/useIsMobile';
import type { AvailableEV } from '../NewBookingProvider';
import EVItemFee from './EVItemFee';
import EVtemDesc from './EVtemDesc';

type Props = {
  data: AvailableEV;
};

export default function EVItem({ data }: Props) {
  const isMobile = useIsMobile();
  const { selectedEVId, setSelectedEVId } = useEVStore();

  const { data: user } = useGetUserProfile();
  const { address, city } = useLocationStore();
  const { expectedEndTime, startTime } = useDateTimeStore();

  const handleSelectEV = (serviceId: string) => {
    setSelectedEVId(serviceId);

    const user_agent = navigator.userAgent;
    const platform = detectPlatform(user_agent);
    trackEvent(EV_TRACK_KEY.ev_vehicle_type, {
      user_id: user?._id || null,
      userId: user?._id || null,
      user_agent,
      platform,
      city_id: city,
      location: address?.address,
      start_time: dayjs.unix(startTime).format(DEFAULT_DATE_TIME_FORMAT),
      end_time: dayjs.unix(expectedEndTime).format(DEFAULT_DATE_TIME_FORMAT),
      hour_renting: (expectedEndTime - startTime) / 3600,
      vehicle: serviceId,
    });
  };

  return (
    <ListItem
      secondaryAction={
        <Radio
          checked={selectedEVId === data?._id}
          onChange={() => handleSelectEV(data?.service_id)}
          value="a"
          name="radio-buttons"
          inputProps={{ 'aria-label': data?.name }}
        />
      }
      sx={{
        minHeight: 192,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: selectedEVId === data?._id ? 'primary.main' : 'grey.100',
        boxShadow: '0px 10px 24px -5px rgba(15, 15, 15, 0.08)',
        borderRadius: 6,
        overflow: 'hidden',
      }}
    >
      <ListItemButton
        data-test-id="selectEvBtn"
        role="button"
        disableRipple
        onClick={
          selectedEVId === data?._id
            ? undefined
            : () => handleSelectEV(data?.service_id)
        }
        className={cn({ 'space-y-2': isMobile })}
        sx={(theme) => ({
          pr: `${theme.spacing(9)} !important`,
          pb: isMobile ? 3 : 1,
          pl: isMobile ? 3 : 2,
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
        })}
      >
        <ListItemAvatar>
          {isMobile ? (
            <Image
              src={
                (data?.photos && data?.photos[0]) ||
                '/static/icons/record/EmptyRecord.svg'
              }
              width={168}
              height={168}
              objectFit="contain"
              className="rounded-l-2xl"
            />
          ) : (
            <Image
              src={
                (data?.photos && data?.photos[0]) ||
                '/static/icons/record/EmptyRecord.svg'
              }
              width={263}
              height={168}
              objectFit="contain"
              className="rounded-l-2xl"
            />
          )}
        </ListItemAvatar>
        <ListItemText
          primary={
            <Stack
              direction={isMobile ? 'column' : 'row'}
              justifyContent="space-between"
              alignItems={isMobile ? 'flex-start' : 'center'}
              spacing={isMobile ? 1 : 2}
            >
              <Stack
                direction="column"
                gap={isMobile ? '8px' : ''}
                spacing={isMobile ? 0 : 0.5}
              >
                <Typography variant={isMobile ? 'h4' : 'h2'} color="grey.700">
                  {data?.vehicle_model?.name}
                </Typography>
                <EVtemDesc data={data} />
              </Stack>

              <Divider
                orientation={isMobile ? 'horizontal' : 'vertical'}
                flexItem
              />
              <EVItemFee data={data} />
            </Stack>
          }
          primaryTypographyProps={{ component: 'div' }}
        />
      </ListItemButton>
    </ListItem>
  );
}
